import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { SideModalComponent } from "../../../components/side-modal/side-modal.component";
import { DataStorageService } from "../../../services/data-storage.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { Observable } from "rxjs";
import { SideModalCloseAction, SideModalComponentTypeEnum } from "../../../models";

type sectionType = 'pet' | 'fnol' | 'account' | 'telemedicine';

@Component({
  selector: 'cpf-desktop-side-subcomponent',
  templateUrl: './desktop-side-subcomponent.component.html',
  styleUrls: ['./desktop-side-subcomponent.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DesktopSideSubcomponentComponent {
  private dialogRef: MatDialogRef<SideModalComponent, any> | undefined;

  private defaultModalConfig: MatDialogConfig = {
    closeOnNavigation: false,
    panelClass: 'side-modal-container',
    position: {
      top: '0',
      right: '0'
    },
    height: '100vh',
    width: '430px'
  }

  constructor(
    public dataStorageService: DataStorageService,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {
    this.route.paramMap.subscribe(paramMap => {
      const section = paramMap.get('section');
      const subsection = paramMap.get('subsection');
      const type = paramMap.get('type');
      const id = paramMap.get('id');
      if (section) {
        this.openSubComponent(section as sectionType, subsection, type, id);
      } else {
        this.navigateHomePage();
      }
    });
  }

  openSubComponent(section: sectionType, subsection: string | null, type: string | null, id: string | null) {
    // `pet` mirrors the mobile `calingo/pet/...` prefix and wraps the pet-scoped subroutes,
    // the same way `account` wraps its own. Here `subsection` is the pet subroute, `id` its param.
    if (section === 'pet') {
      if (subsection === 'invoice' && id) {
        this.openInvoiceAsModal(id);
        return;
      }

      if (subsection === 'claims' && id) {
        this.openClaimAsModal(id);
        return;
      }

      if (subsection === 'fnol-details' && id) {
        this.openFnolDetailsModal(id);
        return;
      }

      if (subsection === 'insurance') {
        if (id === 'details') {
          this.openInsuranceDetailsAsModal();
          return;
        }
        if (id === 'edit-pet-info') {
          this.openEditPetInfoModal();
          return;
        }
        if (id === 'edit-insurance-info') {
          this.openEditInsuranceInfoModal();
          return;
        }
        if (id === 'cancel') {
          this.openInsuranceCancelModal();
          return;
        }
      }

      this.navigateHomePage();
      return;
    }

    if (section === 'fnol') {
      this.openPetFnolModal();
      return;
    }

    if (section === 'account') {
      if (subsection === 'edit') {
        this.openAccountEditModal()
        return;
      }

      if (subsection === 'change-password') {
        this.openAccountChangePasswordModal()
        return;
      }

      if (subsection === 'change-iban') {
        this.openAccountChangeIbanModal()
        return;
      }

      if (subsection === 'forgot-password') {
        this.openForgotPasswordModal()
        return;
      }

      if (subsection === 'contact-us') {
        this.openContactUsModal()
        return;
      }

      if (subsection === 'vouchers') {
        if (id) {
          this.openVoucherDetailsModal(id);
          return;
        }
        this.openVouchersModal()
        return;
      }

      this.openAccountModal();
      return;
    }

    if (section === 'telemedicine') {
      if (subsection === 'chat') {
        this.openChatModal();
        return;
      }
      if (subsection === 'chat-history') {
        this.openChatHistoryModal();
        return;
      }
      if (subsection === 'details' && type && id) {
        this.openChatClaimDetailsModal(type as 'chat' | 'claim', id);
        return;
      }
    }

    // Fallback - change url to current component with no parts after it
    this.navigateHomePage();
  }

  navigateHomePage() {
    // Close with KeepCurrentRoute: this method already performs the navigation
    // below, so the modal's own close must NOT re-trigger navigateHomePage via
    // afterClosed (which a no-arg/undefined close would).
    this.dialogRef?.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home']);
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.dialogRef?.componentInstance?.canDeactivate() ?? true;
  }

  openInvoiceAsModal(invoiceId: string) {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.InvoiceDetail,
          invoiceId: invoiceId
        },
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openClaimAsModal(claimId: string) {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.ClaimDetail,
          claimId: claimId
        },
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openInsuranceDetailsAsModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.ViewInsuranceDetail
        },
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openEditInsuranceInfoModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.EditInsuranceInfo
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openEditPetInfoModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.EditPetInfo
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openPetFnolModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.PetFnol
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openAccountModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.Account
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openAccountEditModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.AccountEdit
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openAccountChangePasswordModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.AccountChangePassword
        },
        disableClose: true,
        ...this.defaultModalConfig
      }
    )

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openAccountChangeIbanModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.AccountChangeIban
        },
        disableClose: true,
        ...this.defaultModalConfig
      }
    )

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openForgotPasswordModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.AccountForgotPassword
        },
        disableClose: true,
        ...this.defaultModalConfig
      }
    )

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openContactUsModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.AccountContactUs
        },
        disableClose: true,
        ...this.defaultModalConfig
      }
    )

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openVouchersModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.AccountVouchers
        },
        disableClose: true,
        ...this.defaultModalConfig
      }
    )

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  openVoucherDetailsModal(voucherId: string) {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.AccountVoucherDetails,
          voucherId: voucherId
        },
        disableClose: true,
        ...this.defaultModalConfig
      }
    )

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  private openFnolDetailsModal(fnolRequestId: string) {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.FnolDetails,
          fnolRequestId: fnolRequestId
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  private openInsuranceCancelModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.CancelInsurance
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }


  ngOnDestroy() {
    // Tearing down the host should not force a home navigation.
    this.dialogRef?.close(SideModalCloseAction.KeepCurrentRoute);
  }

  private openChatModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.Chat
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  private openChatHistoryModal() {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.ChatHistory
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }

  private openChatClaimDetailsModal(type: 'chat' | 'claim', id: string) {
    this.dialogRef = this.dialog.open(SideModalComponent,
      {
        data: {
          type: SideModalComponentTypeEnum.ChatClaimDetails,
          chatClaimDetailsType: type,
          chatClaimDetailsId: id
        },
        disableClose: true,
        ...this.defaultModalConfig
      })

    this.dialogRef.afterClosed().subscribe((action) => action !== SideModalCloseAction.KeepCurrentRoute && this.navigateHomePage());
  }
}
